import React, { useMemo } from 'react';

import { Card, CardStatus } from '@amzn/stencil-react-components/card';
import { Col, Flex, Spacer } from '@amzn/stencil-react-components/layout';
import { MessageBanner, MessageBannerType } from '@amzn/stencil-react-components/message-banner';
import { H1, Text } from '@amzn/stencil-react-components/text';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  LineType,
  DataPointShape,
  Color,
  Stat,
  StatType,
  LineDataSet,
} from '@amzn/stencil-react-components/data-visualization';
import { Hr } from '@amzn/stencil-react-components/layout';
import { useGlobalContext, useUserDataContext } from '../../hooks';
import { WeeklySpprSummary, WeeklySqprSummary, FeedbackParam } from '../../types/backendService';
import { FeedbackStat } from '../shared/FeedbackStat';
import { CHART_SPPR_PERIODS } from '../../constants/productivityConfiguration';
import { NUM_SQPR_PERIODS } from '../../constants/qualityConfiguration';
import { Spinner, SpinnerColorScheme } from '@amzn/stencil-react-components/spinner';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import { IconQuestionCircle } from '@amzn/stencil-react-components/icons';
import { Button, ButtonSize } from '@amzn/stencil-react-components/button';
import moment from 'moment';
import { CT_WAREHOUSE_ALLOW_LIST } from 'src/constants/featureGates';

const IconWithTooltip = withTooltip()(IconQuestionCircle);

export const CompetenciesSummary = ({}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { feedbacks, spprSummaries: spprSummariesContext, sqprSummaries: sqprSummariesContext, auditItems } = useUserDataContext();
  const { userDetails, isAdminViewEnabled } = useGlobalContext();

  const isFeedbacksLoading = feedbacks == undefined;
  const isSpprLoading = spprSummariesContext == undefined;
  const isSqprLoading = sqprSummariesContext == undefined;

  const feedbacksByCategory = useMemo(() => {
    if (isFeedbacksLoading) {
      return new Map<string, number>();
    }

    const output: FeedbackParam[] = feedbacks || [];
    const newFeedbacksByCategory = new Map<string, number>();
    for (const feedback of output) {
      newFeedbacksByCategory.set(
        feedback.feedbackCategory!,
        (newFeedbacksByCategory.get(feedback.feedbackCategory!) || 0) + 1
      );
    }

    return newFeedbacksByCategory;
  }, [feedbacks]);
  const spprSummaries: WeeklySpprSummary[] = spprSummariesContext || [];
  const sqprSummaries: WeeklySqprSummary[] = sqprSummariesContext || [];

  /**
   * Chart displaying SPPR data over a series of weeks.
   */
  const spprSummariesChart = useMemo(() => {
    if (spprSummaries.length == 0) {
      return <></>;
    }

    /**
     * We organize the weekly data in a map so we can efficiently look it up within 'tooltipCustomDetails'
     * The data is keyed by the day of the end of the SPPR week.
     */
    type weekData = { x: number; y: number; label: string; units: string; hours: string; rank: string; };
    const applicableWeeks: Map<number, weekData> = new Map<number, weekData>();
    for (const item of spprSummaries) {
      if (item.rankPercentile != undefined) {
        const value = {
          x: item.endTime!,
          y: -Math.round(item.rankPercentile),
          label: Math.round(item.rankPercentile).toString(),
          units: item.units!.toString(),
          hours: (item.timeWorked! / 3600000).toFixed(1),
          rank: `${item.rank}/${item.totalPopulation}`,
        };
        applicableWeeks.set(value.x!, value);
      }
    }

    /**
     * We want to display a value on the x axis for each of the last NUM_SPPR_PERIODS weeks
     * even if the user does not have SPPR data for that week.
     */
    const currentMoment = moment().tz(userDetails.locale);
    /**
     * Note: startOf('week') returns a Sunday, so we shift by 3 days to get the wednesday of the last SPPR period
     * that has already ended.
     */
    const lastSpprPeriodEnd = currentMoment.subtract(3, 'days').startOf('week').add(3, 'days');
    let tickValues: number[] = [];
    for (let weekLookback = 0; weekLookback < CHART_SPPR_PERIODS; weekLookback++) {
      tickValues.unshift(
        lastSpprPeriodEnd
          .clone()
          .subtract(weekLookback, 'weeks')
          .unix()
      );
    }

    const dataSets: LineDataSet[] = [
      {
        lineType: LineType.Solid,
        dataPointShape: DataPointShape.Circle,
        label: t('percentile'),
        color: Color.Primary,
        data: Array.from(applicableWeeks.values()),
      },
    ];

    return (
      <LineChart
        dataSets={dataSets}
        tooltipCustomDetails={({ x }) => [
          {
            label: t('rank'),
            value: applicableWeeks.get(x as number)!.rank,
          },
          {
            label: t('units'),
            value: applicableWeeks.get(x as number)!.units,
          },
          {
            label: t('hours'),
            value: applicableWeeks.get(x as number)!.hours,
          },
        ]}
        tooltipFormatter={({ x, y }: any) => ({ x: moment(x * 1000).format('M/D'), y: `${-y!}` })}
        height={150}
        shouldFixLabelOverlap={true}
        showBaselineAxis={true}
        showCrosshair={true}
        showDataPoints={true}
        showDependentAxis={true}
        showDependentAxisGridLines={true}
        showLabels={true}
        showLegend={true}
        showTooltip={true}
        baselineAxisTickValues={tickValues}
        dependentAxisTickValues={[-100, -50, 0]}
        baselineAxisFormatter={tick => moment(tick * 1000).format('M/D')}
        dependentAxisFormatter={tick => Math.abs(tick)}
        chartPadding={32}
      />
    );
  }, [spprSummaries]);

  /**
   * Chart displaying SQPR data over a series of weeks.
   */
  const sqprSummariesChart = useMemo(() => {
    if (sqprSummaries.length == 0) {
      return <></>;
    }

    /**
     * We organize the weekly data in a map so we can efficiently look it up within 'tooltipCustomDetails'
     * The data is keyed by the day of the end of the SQPR week.
     */
    type weekData = {
      x: number;
      y: number;
      label: string;
      qualityEvents: string;
      actualDpmo: string;
      expectedDpmo: string;
      rank: string;
      percentile: string;
    };

    let minPerformance = 0;

    const applicableWeeks: Map<number, weekData> = new Map<number, weekData>();
    for (const item of sqprSummaries) {
      if (item.percentile != undefined) {
        const performance = Math.round(item.performance!);
        if (performance < minPerformance) {
          minPerformance = performance;
        }

        const value = {
          x: item.endTime!,
          y: performance,
          label: performance.toString(),
          qualityEvents: item.qualityEvents!.toString(),
          actualDpmo: Math.round(item.currentDPMO!).toString(),
          expectedDpmo: Math.round(item.expectedDPMO!).toString(),
          rank: `${item.rank}/${item.totalPopulation}`,
          percentile: Math.round(item.percentile).toString(),
        };
        applicableWeeks.set(value.x!, value);
      }
    }

    /**
     * We want to display a value on the x axis for each of the last NUM_SQPR_PERIODS weeks
     * even if the user does not have SQPR data for that week.
     */
    const currentMoment = moment().tz(userDetails.locale);
    /**
     * Note: startOf('week') returns a Sunday, so we shift by 3 days to get the wednesday of the last SQPR period
     * that has already ended.
     */
    const lastSqprPeriodEnd = currentMoment
      .subtract(3, 'days')
      .startOf('week')
      .add(3, 'days');
    let tickValues: number[] = [];
    for (let weekLookback = 0; weekLookback < NUM_SQPR_PERIODS; weekLookback++) {
      tickValues.unshift(
        lastSqprPeriodEnd
          .clone()
          .subtract(weekLookback, 'weeks')
          .unix()
      );
    }

    const dataSets: LineDataSet[] = [
      {
        lineType: LineType.Solid,
        dataPointShape: DataPointShape.Circle,
        label: t('performance'),
        color: Color.Primary,
        data: Array.from(applicableWeeks.values()),
      },
    ];

    return (
      <LineChart
        dataSets={dataSets}
        tooltipCustomDetails={({ x }) => {
          let details = [];

          const actualDpmo = applicableWeeks.get(x as number)!.actualDpmo;
          /**
           * Only show percentile and rank if DPMO isn't 0 since otherwise it
           * doesn't have much meaning due to there often being a many-way
           * tie in rank and percentile among associates who have a 0 DPMO.
           */
          if (actualDpmo != undefined && actualDpmo != '0') {
            details.push({
              label: t('percentile'),
              value: applicableWeeks.get(x as number)!.percentile,
            });
          }

          details.push({
            label: t('actualDPMO'),
            value: applicableWeeks.get(x as number)!.actualDpmo,
          });
          details.push({
            label: t('expectedDPMO'),
            value: applicableWeeks.get(x as number)!.expectedDpmo,
          });
          details.push({
            label: t('qualityEvents'),
            value: applicableWeeks.get(x as number)!.qualityEvents,
          });

          return details;
        }}
        tooltipFormatter={({ x, y }: any) => ({ x: moment(x * 1000).format('M/D'), y: y })}
        height={150}
        shouldFixLabelOverlap={true}
        showBaselineAxis={true}
        showCrosshair={true}
        showDataPoints={true}
        showDependentAxis={true}
        showDependentAxisGridLines={true}
        showLabels={true}
        showLegend={true}
        showTooltip={true}
        baselineAxisTickValues={tickValues}
        dependentAxisTickValues={[minPerformance, 50, 100]}
        baselineAxisFormatter={tick => moment(tick * 1000).format('M/D')}
        chartPadding={32}
      />
    );
  }, [sqprSummaries]);

  /**
   * Either display SPPR data, a loading indicator, or verbiage stating that no data is available.
   */
  const spprSummariesChartContent = useMemo(() => {
    if (isSpprLoading) {
      return (
        <Flex height="188px" flexDirection="row" justifyContent="center" alignItems="center">
          <Spinner
            showText
            loadingText={t('loading')}
          />
        </Flex>
      );
    } else if (spprSummaries.length == 0) {
      return (
        <Flex height="188px" flexDirection="row" justifyContent="center" alignItems="center">
          <Text fontSize="T200">{t('noSpprDataShort')}</Text>
        </Flex>
      );
    } else {
      return spprSummariesChart;
    }
  }, [isSpprLoading, spprSummariesChart, t]);

  /**
   * Either display SQPR data, a loading indicator, or verbiage stating that no data is available.
   */
  const sqprSummariesChartContent = useMemo(() => {
    if (isSqprLoading) {
      return (
        <Flex height="188px" flexDirection="row" justifyContent="center" alignItems="center">
          <Spinner
            showText
            loadingText={t('loading')}
          />
        </Flex>
      );
    } else if (sqprSummaries.length == 0) {
      return (
        <Flex height="188px" flexDirection="row" justifyContent="center" alignItems="center">
          <Text fontSize="T200">{t('noSqprDataShort')}</Text>
        </Flex>
      );
    } else {
      return sqprSummariesChart;
    }
  }, [isSqprLoading, sqprSummariesChart, t]);

  return (
    <>
      <Col
        width="100%"
      >
        {/*<MessageBanner*/}
        {/*  type={MessageBannerType.Error}*/}
        {/*>*/}
        {/*  <Text>{t('errorBannerMessage')}</Text>*/}
        {/*</MessageBanner>*/}
      </Col>
      <Flex flexDirection="row" justifyContent="space-between">
        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          <H1>{t('performanceSummary')}</H1>
        </Flex>
        <>
          <Flex flexDirection="row" gridGap="S200">
            <IconWithTooltip
              tooltipText={`${t('positiveFeedbacksTooltip')} ${t('yesterdaysDataTooltip')}`}
            />
            <Card
              data-cy="positive-feedbacks-btn"
              height="96px"
              width={300}
              backgroundColor="green50"
              onClick={() => {
                navigate('/positive-feedbacks');
              }}
            >
              <Flex flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
                <Text fontSize="T300" color="white">
                  {t('positiveFeedbacks')}
                </Text>
                {isFeedbacksLoading && (
                  <Flex height="98px" flexDirection="row" justifyContent="center" alignItems="center">
                    <Spinner colorScheme={SpinnerColorScheme.Inverted} />
                  </Flex>
                )}
                {!isFeedbacksLoading && (
                  <Stat
                    type={StatType.Medium}
                    numeralText={(feedbacksByCategory.get('Positive') || 0).toString()}
                    descriptionText=""
                    isInverted={true}
                  />
                )}
              </Flex>
            </Card>
          </Flex>
        </>
      </Flex>
      <Spacer height={32} />

      <Flex flexDirection="row" gridGap="S400" flexWrap="wrap" justifyContent="center">
        <>
          <Card width={400} status={CardStatus.Default} display="block" isElevated={true}>
            <Col gridGap="S300" alignItems="center">
              <Text textAlign="center" fontSize="T300">
                {t('contributeSafety')}
              </Text>
              <Flex width="100%" flexDirection="row-reverse" style={{ position: 'absolute', right: '16px' }}>
                <IconWithTooltip
                  tooltipText={`${t('safetyTooltip')} ${t('yesterdaysDataTooltip')}`}
                />
              </Flex>
              {isFeedbacksLoading && (
                <Flex height="98px" flexDirection="row" justifyContent="center" alignItems="center">
                  <Spinner
                    showText
                    loadingText={t('loading')}
                  />
                </Flex>
              )}
              {!isFeedbacksLoading && (
                <FeedbackStat feedbacks={feedbacksByCategory.get('Safety')} />
              )}
              <Button data-cy="safety-competency-btn" size={ButtonSize.Small} onClick={() => { navigate('/safety'); }}>
                {t('view')}
              </Button>
            </Col>
          </Card>
        </>
        <>
          <Card width={400} status={CardStatus.Default} display="block" isElevated={true}>
            <Col gridGap="S300" alignItems="center">
              <Text textAlign="center" fontSize="T300">
                {t('manageTime')}
              </Text>
              <Flex width="100%" flexDirection="row-reverse" style={{ position: 'absolute', right: '16px' }}>
                <IconWithTooltip
                  tooltipText={`${t('timeTooltip')} ${t('yesterdaysDataTooltip')}`}
                />
              </Flex>
              {isFeedbacksLoading && (
                <Flex height="98px" flexDirection="row" justifyContent="center" alignItems="center">
                  <Spinner
                    showText
                    loadingText={t('loading')}
                  />
                </Flex>
              )}
              {!isFeedbacksLoading && (
                <FeedbackStat feedbacks={feedbacksByCategory.get('Time')} />
              )}
              <Button data-cy="time-competency-btn" size={ButtonSize.Small}
                onClick={() => {
                  navigate('/time');
                }}
              >
                {t('view')}
              </Button>
            </Col>
          </Card>
        </>
        <>
          <Card width={400} status={CardStatus.Default} display="block" isElevated={true}>
            <Col gridGap="S300" alignItems="center">
              <Text textAlign="center" fontSize="T300">
                {t('followStandards')}
              </Text>
              <Flex width="100%" flexDirection="row-reverse" style={{ position: 'absolute', right: '16px' }}>
                <IconWithTooltip
                  tooltipText={`${t('policyTooltip')} ${t('yesterdaysDataTooltip')}`}
                />
              </Flex>
              {isFeedbacksLoading && (
                <Flex height="98px" flexDirection="row" justifyContent="center" alignItems="center">
                  <Spinner
                    showText
                    loadingText={t('loading')}
                  />
                </Flex>
              )}
              {!isFeedbacksLoading && (
                <FeedbackStat feedbacks={feedbacksByCategory.get('Standards')} />
              )}
              <Button data-cy="standards-competency-btn" size={ButtonSize.Small} onClick={() => { navigate('/standards');}}>
                {t('view')}
              </Button>
            </Col>
          </Card>
        </>
        <>
          <Card width={400} status={CardStatus.Default} display="block" isElevated={true}>
            <Col gridGap="S300" alignItems="center">
              <Text textAlign="center" fontSize="T300">
                {t('ensureQuality')}
              </Text>
              <Flex width="100%" flexDirection="row-reverse" style={{ position: 'absolute', right: '16px' }}>
                <IconWithTooltip
                  tooltipText={`${t('qualityTooltip')} ${t('yesterdaysDataTooltip')}`}
                />
              </Flex>
              {sqprSummariesChartContent}
            </Col>
            <Hr size="wide" />
            <Col gridGap="S300" alignItems="center">
              {isFeedbacksLoading && (
                <Flex height="98px" flexDirection="row" justifyContent="center" alignItems="center">
                  <Spinner
                    showText
                    loadingText={t('loading')}
                  />
                </Flex>
              )}
              {!isFeedbacksLoading && (
                <FeedbackStat feedbacks={feedbacksByCategory.get('Quality')} />
              )}
              <Button data-cy="quality-competency-btn" size={ButtonSize.Small}
                onClick={() => {
                  navigate('/quality');
                }}
              >
                {t('view')}
              </Button>
            </Col>
          </Card>
        </>
        <Card width={400} status={CardStatus.Default} display="block" isElevated={true}>
          <Col gridGap="S300" alignItems="center">
            <Text textAlign="center" fontSize="T300">
              {t('contributeTeam')}
            </Text>
            <Flex width="100%" flexDirection="row-reverse" style={{ position: 'absolute', right: '16px' }}>
              <IconWithTooltip
                tooltipText={`${t('productivityTooltip')} ${t('yesterdaysDataTooltip')}`}
              />
            </Flex>
            {spprSummariesChartContent}
          </Col>
          <Hr size="wide" />
          <Col gridGap="S300" alignItems="center">
            <>
              {isFeedbacksLoading && (
                <Flex height="98px" flexDirection="row" justifyContent="center" alignItems="center">
                  <Spinner
                    showText
                    loadingText={t('loading')}
                  />
                </Flex>
              )}
              {!isFeedbacksLoading && (
                <FeedbackStat feedbacks={feedbacksByCategory.get('Productivity')} />
              )}
            </>
            <Button data-cy="productivity-competency-btn" size={ButtonSize.Small} onClick={() => {
                navigate('/productivity');
              }}
            >
              {t('view')}
            </Button>
          </Col>
        </Card>
        {(CT_WAREHOUSE_ALLOW_LIST.includes(userDetails.warehouseId) || (userDetails.isAdmin && isAdminViewEnabled)) && (
          <Card width={400} status={CardStatus.Default} display="block" isElevated={true}>
            <Col gridGap="S300" alignItems="center">
              <Text textAlign="center" fontSize="T300">
                {t('auditTitle')}
              </Text>
              <Stat
                type={StatType.Large}
                numeralText={(auditItems?.length || 0).toString()}
                descriptionText={t('auditTitle')}
              />
              <Button data-cy="audit-competency-btn" size={ButtonSize.Small} onClick={() => { navigate('/audits'); }}>
                {t('view')}
              </Button>
            </Col>
          </Card>
        )}
      </Flex>
    </>
  );
};
